import { decodeOrElse } from 'app/utils/ioTs';
import {
  SelectionActionCodec,
  SelectionActionType,
} from '@engoo/realtime-protocol/selection';
import { parseStateKey } from 'app/models/entityStateSync/keypath';
import { SharedSelectionDocumentState, Selection } from 'app/models/rtc/selection/constants';
import { List } from 'immutable';
import * as iots from 'io-ts';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';

const SelectionActionWithUserDecoder = iots.intersection([
  SelectionActionCodec,
  // Work-around for `user` on IPersistedAction being `string` instead of `EntityString`
  // https://github.com/iknow/issues/issues/1999
  iots.type({ user: IdentityEntityStringIdCodec }),
]).asDecoder();

/**
 * This reducer's main functionality is to normalize selections
 * into how they're more useful in the app.
 *
 * Updates coming from the server contain single selection for each
 * user, each selection having multiple paths for selected parts
 * of the document. This reducer inverts that and for each keypath
 * creates a `participant => selection` map.
 */
export function selectionReducer(
  state: SharedSelectionDocumentState = new SharedSelectionDocumentState(),
  encodedAction?: unknown,
): SharedSelectionDocumentState {
  const action = decodeOrElse(SelectionActionWithUserDecoder, encodedAction, () => null);

  if (!action) {
    return state;
  }

  const { user } = action;

  return state.update('selections', (selections) => {
    switch (action.type) {
      case SelectionActionType.Clear:
        return selections.delete(user);
      case SelectionActionType.Set: {
        const { paths, ...selection } = action;
        const syncLocalSelection = new Selection({
          paths: List(paths).map(parseStateKey),
          ...selection,
        });

        return selections.set(user, syncLocalSelection);
      }
    }
  });
}
